/**
 * Footer 컴포넌트 - 사이트 하단 정보
 * 회사 정보, 이용약관, 개인정보처리방침 등을 표시
 */
// import { Link } from 'react-router-dom'; // 웹빌더에서는 라우터 미사용
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(useGSAP);

interface FooterProps {
    companyName?: string;
    logoUrl?: string;
    address?: string;
    phone?: string;
    email?: string;
    businessNumber?: string;
    onlineSalesNumber?: string;
    hostingProvider?: string;
    ceo?: string;
    privacyOfficer?: string;
}

export function Footer({
    companyName = 'Marijour',
    logoUrl = '',
    address = '서울특별시 강남구 테헤란로 123',
    phone = '1234-5678',
    email = 'example@example.com',
    businessNumber = '211-88-81810',
    onlineSalesNumber = '2025-서울강남-12345',
    hostingProvider = '아무개',
    ceo = '홍길동',
    privacyOfficer = '홍길동'
}: FooterProps) {
    const COMPANY_INFO_DATA = [
        [
            { title: '상호', content: companyName, type: 'text' },
            { title: '대표', content: ceo, type: 'text' },
            { title: '개인정보관리책임자', content: privacyOfficer, type: 'text' },
        ],
        [
            { title: '전화', content: phone, type: 'tel' },
            { title: '이메일', content: email, type: 'email' },
            { title: '주소', content: address, type: 'text' },
        ],
        [
            { title: '사업자등록번호', content: businessNumber, type: 'href' },
            { title: '통신판매업신고번호', content: onlineSalesNumber, type: 'text' },
            { title: '호스팅 제공자', content: hostingProvider, type: 'text' },
        ],
    ];
    useGSAP(() => {
        gsap.to('#footer', {
            opacity: 1,
            duration: 0.9,
            ease: 'power2.out',
        });
    });

    return (
        <footer
            id="footer"
            className=""
        >
            <div className="globalWrapper border-t border-black/10 py-8 md:py-10">
                <h2 className="text-3xl font-serif leading-logo">
                    {logoUrl ? (
                        <img 
                            src={logoUrl} 
                            alt={companyName} 
                            className="max-h-[50px] w-auto object-contain"
                            onError={(e) => {
                                console.error("푸터 로고 이미지 로드 오류");
                                // 이미지 로드 실패 시 텍스트로 표시
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const textSpan = document.createElement('span');
                                textSpan.textContent = companyName;
                                target.parentElement?.appendChild(textSpan);
                            }}
                        />
                    ) : (
                        companyName
                    )}
                </h2>
                <nav className="mt-9">
                    <ul className="flex flex-wrap gap-6 leading-body">
                        <li>
                            <a
                                href="/terms"
                                className="text-xs md:text-sm text-black hover-primary"
                            >
                                이용약관
                            </a>
                        </li>
                        <li>
                            <a
                                href="/privacy-policy"
                                className="text-xs md:text-sm text-black hover-primary"
                            >
                                개인정보처리방침
                            </a>
                        </li>
                        {businessNumber && (
                            <li>
                                <a
                                    href={`https://www.ftc.go.kr/bizCommPop.do?wrkr_no=${businessNumber.replace(/-/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs md:text-sm text-black hover-primary"
                                >
                                    사업자정보확인
                                </a>
                            </li>
                        )}
                        {/*<li>*/}
                        {/*    <a*/}
                        {/*        href="/qna"*/}
                        {/*        className="text-xs md:text-sm text-black hover-primary"*/}
                        {/*    >*/}
                        {/*        1:1 문의*/}
                        {/*    </a>*/}
                        {/*</li>*/}
                    </ul>
                </nav>
                <div className="companyInfo text-black/80 leading-body text-xs space-y-1 mt-6">
                    {COMPANY_INFO_DATA.map((row, rowIndex) => (
                        <div
                            key={rowIndex}
                            className="flex flex-wrap items-center"
                        >
                            {row.map((item, itemIndex) => (
                                <dl
                                    key={item.title}
                                    className="flex items-center whitespace-nowrap"
                                >
                                    <dt className="inline">{item.title} :</dt>
                                    <dd className="inline ml-1">
                                        {item.type === 'email' ? (
                                            <a
                                                href={`mailto:${item.content}`}
                                                className="hover-primary"
                                            >
                                                {item.content}
                                            </a>
                                        ) : item.type === 'tel' ? (
                                            <a
                                                href={`tel:${item.content}`}
                                                className="hover-primary"
                                            >
                                                {item.content}
                                            </a>
                                        ) : item.type === 'href' ? (
                                            <a
                                                href={`https://www.ftc.go.kr/bizCommPop.do?wrkr_no=${item.content.replace(/-/g, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hover-primary"
                                            >
                                                {item.content}
                                            </a>
                                        ) : (
                                            item.content
                                        )}
                                    </dd>
                                    {itemIndex < row.length - 1 && <span className="text-black/20 px-1.5">|</span>}
                                </dl>
                            ))}
                        </div>
                    ))}
                </div>
                <p className="text-black/40 text-xs leading-body mt-6">©{companyName}. {new Date().getFullYear()} ALL RIGHTS RESERVED.</p>
            </div>
        </footer>
    );
}
